import { type FC } from "react";
import { Dialog, LinearProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreateCountryAccessMutation } from "@/partner/entities/countryAccesses";
import {
  mapCountryAccessToFormValues,
  mapFormValuesToCreateCountryAccessInput,
  type CountryAccessFormValues,
} from "../model";
import { CountryAccessForm } from "./CountryAccessForm";

interface CreateCountryAccessDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

export const CreateCountryAccessDialog: FC<CreateCountryAccessDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [createCountryAccess, { loading }] = useCreateCountryAccessMutation({
    onCompleted: () => {
      enqueueSnackbar(t("countryAccesses.create.success"), {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["CountryAccessesList", "CountryAccessesSelect"],
  });

  const handleSubmit = async (values: CountryAccessFormValues) => {
    await createCountryAccess({
      variables: {
        input: mapFormValuesToCreateCountryAccessInput(values),
      },
    });

    onCompleted?.();
  };

  const initialValues = mapCountryAccessToFormValues(null);

  return (
    <Dialog fullScreen scroll="body" open={open} onClose={onClose}>
      {loading && <LinearProgress />}
      <CountryAccessForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={t("countryAccesses.create.title")}
        loading={loading}
      />
    </Dialog>
  );
};
